import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Clock, CheckCircle2, XCircle, Loader2, ShoppingCart, Split, Plus, X } from "lucide-react";
import { myApi } from "@/my/api";

const ESTADO_META = {
  RASCUNHO: { label: "Rascunho", cls: "bg-slate-100 text-slate-700" },
  EM_DECISAO: { label: "Em decisão", cls: "bg-amber-100 text-amber-800" },
  CONVERTIDA: { label: "Convertida", cls: "bg-emerald-100 text-emerald-700" },
  CANCELADA: { label: "Cancelada", cls: "bg-rose-100 text-rose-700" },
};

const fmtEur = (v) => new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(Number(v || 0));
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("pt-PT") : "—");

export default function MyPropostas() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deciding, setDeciding] = useState(null); // id da proposta em decisão
  const [busy, setBusy] = useState(false);
  const [fiveModal, setFiveModal] = useState(null); // proposta em fluxo 50/50

  const load = async () => {
    setLoading(true);
    try {
      const r = await myApi.propostasList();
      setRows(r.propostas || []);
    } catch (e) {
      toast.error(e.message || "Erro ao carregar propostas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const cancelar = async (id) => {
    if (!window.confirm("Tens a certeza que queres cancelar esta proposta?")) return;
    setBusy(true);
    try {
      await myApi.propostaCancelar(id);
      toast.success("Proposta cancelada");
      load();
    } catch (e) { toast.error(e.message || "Erro ao cancelar"); }
    finally { setBusy(false); }
  };

  const decidir = async (id, modo) => {
    if (modo === "5050") { const p = rows.find((x) => x.ID_Proposta === id); setFiveModal(p || { ID_Proposta: id }); setDeciding(null); return; }
    setBusy(true);
    try {
      const r = await myApi.propostaDecidir(id, modo);
      if (modo === "pronto") {
        toast.success("Produtos adicionados ao carrinho!");
        navigate(r.redirect || "/my/cart");
      }
    } catch (e) {
      toast.error(e.message || "Erro ao decidir");
    } finally { setBusy(false); setDeciding(null); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="my-propostas-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-teal-600 flex items-center justify-center">
          <FileText className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">As minhas propostas</h1>
          <p className="text-sm text-slate-500">Gere, decide ou cancela as tuas propostas corporate.</p>
        </div>
      </div>

      <div className="flex gap-1 p-1 rounded-full bg-slate-100 w-fit mb-6" data-testid="propostas-tabs">
        <Link to="/my/eventos" data-testid="tab-nova-proposta"
          className="px-4 py-2 rounded-full text-sm font-medium text-slate-500 hover:text-slate-900">Nova proposta</Link>
        <Link to="/my/propostas" data-testid="tab-minhas-propostas"
          className="px-4 py-2 rounded-full text-sm font-medium bg-teal-600 text-white">As minhas propostas</Link>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-16 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> A carregar…
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-200" data-testid="propostas-vazio">
          <p className="text-slate-600 mb-4">Ainda não guardaste nenhuma proposta.</p>
          <button onClick={() => navigate("/my/eventos")} data-testid="criar-proposta-btn"
            className="px-5 py-2.5 rounded-full bg-teal-600 text-white font-medium hover:bg-teal-700 transition-colors">
            Criar proposta
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {rows.map((p) => {
            const meta = ESTADO_META[p.Estado] || ESTADO_META.RASCUNHO;
            const aberta = p.Estado === "EM_DECISAO" || p.Estado === "RASCUNHO";
            return (
              <div key={p.ID_Proposta} data-testid={`proposta-${p.ID_Proposta}`}
                className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-sm text-slate-500">{p.Referencia}</span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${meta.cls}`}
                        data-testid={`proposta-estado-${p.ID_Proposta}`}>{meta.label}</span>
                    </div>
                    <h3 className="font-semibold text-slate-900">{p.TituloPrograma || "Proposta Corporate"}</h3>
                    <p className="text-sm text-slate-500">{p.NomeClienteEmpresa}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-slate-900">{fmtEur(p.TotalFinal)}</div>
                    {p.ValidaAte && (
                      <div className="text-xs text-slate-500 flex items-center gap-1 justify-end mt-1">
                        <Clock className="w-3 h-3" /> Decisão até {fmtDate(p.ValidaAte)}
                        {typeof p.dias_para_decisao === "number" && aberta && (
                          <span className={p.dias_para_decisao <= 7 ? "text-rose-600 font-semibold" : ""}>
                            &nbsp;({p.dias_para_decisao}d)
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {(() => {
                  const podeDecidir = aberta || (p.Estado === "CONVERTIDA" && p.editavel);
                  const pagaBloqueada = p.Estado === "CONVERTIDA" && !p.editavel;
                  return (
                    <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
                      {p.encomendas?.length > 0 && (
                        <div className="text-sm text-slate-600" data-testid={`encomendas-${p.ID_Proposta}`}>
                          <span className="text-slate-400">Encomendas geradas:</span>{" "}
                          {p.encomendas.map((oid) => (
                            <a key={oid} href={`/my/encomendas/${oid}`} data-testid={`encomenda-link-${oid}`}
                              className="inline-block font-mono font-semibold text-teal-700 hover:text-teal-900 underline bg-slate-100 rounded px-1.5 py-0.5 mr-1">#{oid}</a>
                          ))}
                        </div>
                      )}
                      {p.Estado === "CONVERTIDA" && p.editavel && (
                        <p className="text-xs text-slate-500" data-testid={`reabrir-hint-${p.ID_Proposta}`}>
                          Enquanto nenhuma encomenda estiver paga, podes voltar a decidir (mudar entre "a pronto" e 50/50) e gerar novas encomendas.
                        </p>
                      )}

                      {podeDecidir ? (
                        deciding === p.ID_Proposta ? (
                          <div className="flex flex-wrap gap-2 items-center" data-testid={`decidir-opcoes-${p.ID_Proposta}`}>
                            <span className="text-sm text-slate-600 mr-1">Como queres pagar?</span>
                            <button disabled={busy} onClick={() => decidir(p.ID_Proposta, "pronto")}
                              data-testid={`decidir-pronto-${p.ID_Proposta}`}
                              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 disabled:opacity-50">
                              <ShoppingCart className="w-4 h-4" /> A pronto
                            </button>
                            <button disabled={busy} onClick={() => decidir(p.ID_Proposta, "5050")}
                              data-testid={`decidir-5050-${p.ID_Proposta}`}
                              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-teal-600 text-teal-700 text-sm font-medium hover:bg-teal-50 disabled:opacity-50">
                              <Split className="w-4 h-4" /> 50% + 50%
                            </button>
                            <button onClick={() => setDeciding(null)} className="text-sm text-slate-400 hover:text-slate-600 px-2">Voltar</button>
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            <button onClick={() => setDeciding(p.ID_Proposta)} data-testid={`decidir-btn-${p.ID_Proposta}`}
                              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-teal-600 text-white text-sm font-medium hover:bg-teal-700">
                              <CheckCircle2 className="w-4 h-4" /> {p.Estado === "CONVERTIDA" ? "Gerar nova encomenda" : "Decidir"}
                            </button>
                            <button onClick={() => navigate(`/my/eventos?proposta=${p.ID_Proposta}`)}
                              data-testid={`editar-btn-${p.ID_Proposta}`}
                              className="px-4 py-2 rounded-full border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50">
                              Editar
                            </button>
                            <button onClick={() => cancelar(p.ID_Proposta)} disabled={busy} data-testid={`cancelar-btn-${p.ID_Proposta}`}
                              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-rose-300 text-rose-700 text-sm font-medium hover:bg-rose-50 disabled:opacity-50">
                              <XCircle className="w-4 h-4" /> Cancelar
                            </button>
                          </div>
                        )
                      ) : pagaBloqueada ? (
                        <div className="flex flex-wrap gap-2 items-center">
                          <button onClick={() => navigate(`/my/eventos?proposta=${p.ID_Proposta}`)}
                            data-testid={`ver-btn-${p.ID_Proposta}`}
                            className="px-4 py-2 rounded-full border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50">
                            Visualizar
                          </button>
                          <span className="text-xs text-slate-500" data-testid={`so-leitura-${p.ID_Proposta}`}>
                            Encomenda paga — apenas visualização
                          </span>
                        </div>
                      ) : (
                        <button onClick={() => navigate(`/my/eventos?proposta=${p.ID_Proposta}`)}
                          data-testid={`ver-btn-${p.ID_Proposta}`}
                          className="px-4 py-2 rounded-full border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50">
                          Visualizar
                        </button>
                      )}
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      )}

      {fiveModal && (
        <Billing5050Modal
          proposta={fiveModal}
          onClose={() => setFiveModal(null)}
          onDone={() => { setFiveModal(null); load(); }}
        />
      )}
    </div>
  );
}

function Billing5050Modal({ proposta, onClose, onDone }) {
  const [moradas, setMoradas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ Nome: "", Nif: "", Morada: "", Localidade: "", CodigoPostal: "", ID_Pais: "PRT" });
  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const loadMoradas = async () => {
    setLoading(true);
    try {
      const all = await myApi.moradas();
      const fat = (all || []).filter((m) => m.ID_TipoMorada === "F");
      setMoradas(fat);
      if (fat.length) setSel(fat[0].ID_ClienteMorada);
      setAdding(fat.length === 0);
    } catch (e) { toast.error(e.message || "Erro ao carregar moradas"); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadMoradas(); }, []);

  const criarMorada = async () => {
    if (!form.Morada || !form.Localidade || !form.CodigoPostal) { toast.error("Preencha morada, localidade e código postal."); return; }
    setBusy(true);
    try {
      await myApi.moradaCreate({ ID_TipoMorada: "F", ...form, Principal: moradas.length === 0 ? "S" : "N" });
      toast.success("Morada de faturação criada.");
      setAdding(false);
      await loadMoradas();
    } catch (e) { toast.error(e.message || "Erro ao criar morada"); }
    finally { setBusy(false); }
  };

  const gerar = async () => {
    if (!sel) { toast.error("Escolha uma morada de faturação."); return; }
    setBusy(true);
    try {
      const r = await myApi.proposta5050(proposta.ID_Proposta, sel);
      const ids = (r.encomendas || []).map((e) => e.id_encomenda).join(" e ");
      toast.success(`2 encomendas criadas (a aguardar pagamento): #${ids}`);
      onDone();
    } catch (e) { toast.error(e.message || "Não foi possível gerar as encomendas."); }
    finally { setBusy(false); }
  };

  const inp = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="billing-5050-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-xl max-h-[90vh] overflow-auto">
        <div className="flex items-start justify-between mb-1">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2"><Split className="w-5 h-5 text-teal-600" /> Pagamento 50% + 50%</h3>
          <button onClick={onClose} data-testid="billing-5050-close" className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-slate-500 mb-4">Serão geradas <b>2 encomendas iguais</b> (cada uma com 50% do valor) a <b>aguardar pagamento</b> por Transferência Bancária. Escolha a morada de faturação.</p>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 py-8 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> A carregar…</div>
        ) : (
          <div className="space-y-3">
            {moradas.map((m) => (
              <label key={m.ID_ClienteMorada} data-testid={`morada-opt-${m.ID_ClienteMorada}`}
                className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer ${sel === m.ID_ClienteMorada ? "border-teal-600 bg-teal-50" : "border-slate-200"}`}>
                <input type="radio" name="morada" className="mt-1" checked={sel === m.ID_ClienteMorada} onChange={() => setSel(m.ID_ClienteMorada)} />
                <div className="text-sm">
                  <div className="font-semibold text-slate-800">{m.Nome || "Morada de faturação"}{m.Nif ? ` · NIF ${m.Nif}` : ""}</div>
                  <div className="text-slate-500">{m.Morada}, {m.CodigoPostal} {m.Localidade}</div>
                </div>
              </label>
            ))}

            {adding ? (
              <div className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid="morada-form">
                <div className="grid grid-cols-2 gap-2">
                  <input className={inp} placeholder="Nome / Empresa" data-testid="morada-nome" value={form.Nome} onChange={(e) => upd("Nome", e.target.value)} />
                  <input className={inp} placeholder="NIF" data-testid="morada-nif" value={form.Nif} onChange={(e) => upd("Nif", e.target.value)} />
                </div>
                <input className={inp} placeholder="Morada" data-testid="morada-morada" value={form.Morada} onChange={(e) => upd("Morada", e.target.value)} />
                <div className="grid grid-cols-2 gap-2">
                  <input className={inp} placeholder="Código Postal" data-testid="morada-cp" value={form.CodigoPostal} onChange={(e) => upd("CodigoPostal", e.target.value)} />
                  <input className={inp} placeholder="Localidade" data-testid="morada-localidade" value={form.Localidade} onChange={(e) => upd("Localidade", e.target.value)} />
                </div>
                <div className="flex gap-2 justify-end">
                  {moradas.length > 0 && <button onClick={() => setAdding(false)} className="text-sm text-slate-500 px-3 py-1.5">Cancelar</button>}
                  <button onClick={criarMorada} disabled={busy} data-testid="morada-guardar"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-800 text-white text-sm disabled:opacity-50">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Guardar morada
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => setAdding(true)} data-testid="morada-add-toggle"
                className="inline-flex items-center gap-1.5 text-sm text-teal-700 hover:underline">
                <Plus className="w-4 h-4" /> Adicionar nova morada de faturação
              </button>
            )}

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
              <button onClick={onClose} className="px-4 py-2 rounded-full border border-slate-300 text-slate-700 text-sm">Cancelar</button>
              <button onClick={gerar} disabled={busy || !sel || adding} data-testid="gerar-5050-btn"
                className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Gerar 2 encomendas (50/50)
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
